import { loadEmailTemplates } from "./utils/emailTemplateLoader";
import { loadTeamDefaults } from "./utils/teamDefaults";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import {
  onAuthStateChanged,
  signOut,
  getAuth, // ✅ Local Persistence
} from "firebase/auth";
import { auth, authStartedWithoutPopupHelper, messagingReady } from "./firebase/firebaseConfig";
import { toast, ToastContainer } from "react-toastify";
import { actionType } from "./context/reducer";
import { startLaunchReads } from "./utils/firebasefunctions";
import { isFastLaneReady, loadSignedInUserFast, startFastLaneDownload } from "./utils/fastUserRead";
import { clearLaunchData } from "./utils/launchData";
import { clearContactCache } from "./utils/contactCache";
import { useStateValue } from "./context/StateProvider";
import { onMessage } from "firebase/messaging";
import Message from "./components/Message";
import PortalLoading from "./components/client-portal/PortalLoading";
import NewVersionBanner from "./components/NewVersionBanner";
import MfaEnrollGate from "./components/auth/MfaEnrollGate";
import CantReachScreen from "./components/auth/CantReachScreen";
import ScreenLoading from "./components/ScreenLoading";
import NoSignalBar from "./components/NoSignalBar";
import EmailReviewHost from "./components/email/EmailReviewHost";
import { isMfaEnrolled } from "./utils/mfa";
import {
  browserIsOffline,
  forgetWaitingPages,
  lazyPage,
  pageOpenMark,
  whenSignalIsUsable,
} from "./utils/lazyPage";
import { canReachSite } from "./utils/siteReachable";
import {
  forgetLastRole,
  readSignedInBefore,
  rememberLastRole,
  rememberSignedInBefore,
  shouldReloadForGoogleSignIn,
  startLaunchDownloads,
} from "./utils/lastRole";
import { RETRY_RELOAD_AT_KEY, reloadPage, takeReloadNotice } from "./utils/reloadPage";
import { forgetWaitingSaves } from "./utils/saveWhenConnected";
import { isTextingEnabled, subscribeTextingFlag, watchTextingFlag } from "./utils/textingSwitch";
import { watchOwnAccount } from "./utils/accountWatch";
import { takeReturnTo } from "./utils/agentSession";
import { appRouteFromAlertUrl } from "./utils/alertLink";

// Each screen is its own download (utils/lazyPage.js), so opening the app no
// longer means fetching every page before anything can show.
const Dashboard = lazyPage(() => import("./pages/dashboard"));
const Login = lazyPage(() => import("./pages/Login"));
const Signup = lazyPage(() => import("./pages/Signup"));
const Privacypolicy = lazyPage(() => import("./pages/Privacypolicy"));
const Termsandservice = lazyPage(() => import("./pages/Termsandservice"));
const PublicFeedback = lazyPage(() => import("./pages/public-feedback"));
const ClientPortal = lazyPage(() => import("./pages/client-portal"));
// 2026-09-16 (open house requests): the seller's decision page, public. 2026-09-18: the agent website
// (/#/openhouses), public too; the retired hosting link (/#/host) redirects to it.
const AgentSite = lazyPage(() => import("./pages/openhouses"));
const OhDecide = lazyPage(() => import("./pages/oh-decide"));
// 2026-09-19 (Closing Wrap-Up): the seller's New Address page, public.
const NewAddress = lazyPage(() => import("./pages/new-address"));
// 2026-09-23 (Referral Tracker): the receiving agent's Referral Agreement page, public.
const ReferralSign = lazyPage(() => import("./pages/referral-sign"));
// The seller's listing appointment Confirm / Reschedule page (October 6th Meeting #8). Public, like the two above.
const ApptConfirm = lazyPage(() => import("./pages/appt-confirm"));
// The Stager Portal (10/12/2026): the stager's private inventory page (/#/stager?t=), public like the portal.
const StagerPortal = lazyPage(() => import("./pages/stager-portal"));

// Staff opening the installed app are almost always still signed in, so start the
// dashboard's download now, alongside sign-in, instead of after it, together with
// the first page they will see: Home, for everyone (Client Care open on their
// Dashboard since 09/27/2026, not Transactions; utils/lastRole.js).
// Clients on a portal or feedback link never need any of it, and the test runner
// downloads nothing early.
startLaunchDownloads({
  env: process.env.NODE_ENV,
  hash: window.location.hash,
  preload: {
    // The quick reader that fetches the signed-in person's own record (utils/fastUserRead.js)
    // is its own small download. Started here rather than at sign-in, where it would sit on
    // the critical path of that very read and, after every upload, be a cold download racing
    // the deals download. This only fetches code: it opens no connection and reads no
    // document, so nothing is read before sign-in. utils/lastRole.js starts it only on a
    // device somebody has signed in on before, the only kind that can ever use it.
    fastLane: () => startFastLaneDownload(),
    dashboard: () => Dashboard.preload(),
    home: () => import("./pages/home"),
    // Client Care's dashboard, which Home loads on its own: started only for a remembered Client Care.
    supervisorHome: () => import("./components/home/SupervisorDashboard"),
    // Only on a device somebody has signed in on, and last: Sign Out on the Can't Reach screen
    // needs it with no signal, and a device that stays signed in never fetched it otherwise.
    login: () => import("./pages/Login"),
  },
});

// How long Sign Out on the Can't Reach screen waits for Firebase before showing the login
// page anyway (09/16/2026, second review round). Firebase answers a sign-out at once
// whenever its own start-up has finished. After the 12 second wait below it may not have,
// and the sign-out then waits in line behind it, possibly for ever.
const SIGN_OUT_WAIT_MS = 5000;

function App() {
  const [user, setUser] = useState(null);
  const [, dispatch] = useStateValue();
  const [loading, setLoading] = useState(true);
  // Two-step ENROLLMENT gate PAUSED (Henry's call, night of 2026-07-27,
  // while agent phones/carriers settle): unenrolled users go straight in.
  // Already-enrolled users still get their SMS code at login — that part is
  // enforced by Firebase and keeps working. Set to false to resume forcing
  // enrollment for everyone.
  const MFA_ENROLLMENT_PAUSED = true;
  // Two-step verification: true once this login's Firebase user has an
  // enrolled second factor. False forces the enrollment gate before the
  // dashboard. Session-scoped only; Firebase itself enforces the factor at
  // sign-in for enrolled users.
  const [mfaOk, setMfaOk] = useState(true);
  // Our agent tapped "Use Your CRM Login" on the agent website with nobody signed in, and has just signed in
  // on the login page: send them back there (utils/agentSession.js takeReturnTo, read once, the moment the
  // sign-in lands). Only ever the agent website, and only for 30 minutes.
  const [returnToAgentSite, setReturnToAgentSite] = useState(false);
  // Signed in, but the person's own record could not be loaded because the server
  // could not be reached (weak or no signal). Shows the Can't Reach screen, not the
  // login page (2026-09-14, speed wave 4).
  const [unreachable, setUnreachable] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // When the last attempt failed, so the Can't Reach screen can say so and name the time
  // (09/16/2026 review: a failed Retry used to leave the screen looking untouched, which
  // is indistinguishable from a tap that never registered). Null while no attempt has
  // failed, and cleared again the moment one gets through.
  const [lastFailedAt, setLastFailedAt] = useState(null);
  const [cantReachReason, setCantReachReason] = useState("record");
  // The same, as of this instant, for a Retry tap that has to wait for the site check (fifth review round).
  const cantReachWhy = useRef("record");
  // Has Firebase's sign-in reported anything at all, this launch? Until it has, the one real retry on the
  // "no-answer" screen is a fresh start; once it has, the account's record is being read, and a reload would
  // throw that read away (fifth review round).
  const firebaseAnswered = useRef(false);
  // Who Firebase last reported as signed in, and how to try letting them in again.
  const signedInUser = useRef(null);
  const retrySignIn = useRef(null);
  const retryInFlight = useRef(false);
  // Did the last attempt reach the server at all? A ref, not state: retry() below reads it
  // the instant the attempt finishes, where a state value would still be the old one.
  const lastAttemptReached = useRef(false);
  // Why a deleted or disabled account was just signed out, kept for the page the
  // one sign-out reload opens (utils/reloadPage.js), so the login page still says why.
  const blockedNotice = useRef(null);
  // `unreachable` as of this instant, for retry() below, which must know it the moment an
  // attempt finishes rather than on the next render.
  const cantReachShowing = useRef(false);

  // Sign Out on the Can't Reach screen is running (09/16/2026, second review round). The
  // state drives the button's "Signing Out..."; the ref stops a second tap at once.
  const [signingOut, setSigningOut] = useState(false);
  const signingOutNow = useRef(false);
  // The timer that shows the login page if Firebase never answers that sign-out.
  const signOutWait = useRef(null);
  // Set from that Sign Out tap until Firebase reports nobody signed in, or the sign-out
  // settles. A signed-in answer arriving in that time is the account being left (Firebase's
  // start-up finishing while the sign-out waits in line behind it), so it is not let in.
  const leavingAccount = useRef(false);

  // The one reload after a sign-out, while it waits for the connection to come back: what
  // calls the wait off. And whether the login page should say that Google sign-in is waiting too.
  const reloadWaitingForSignal = useRef(null);
  const [googleWaitsForSignal, setGoogleWaitsForSignal] = useState(false);

  // The client portal is a public page for clients, not staff, so it gets none of the CRM's furniture
  // (spec 3.8). Read through useLocation, not window.location, so App re-renders when a staff member opens
  // a portal link from inside the CRM.
  const location = useLocation();
  const onPortal = location.pathname.startsWith("/portal");
  // The agent website (and the old hosting link that redirects to it) and the seller's decision page are public
  // too: no New Version bar, and never the Can't Reach screen (none of them needs a staff account). The portal
  // keeps its own navy wait (onPortal).
  const onPublicPage = /^\/(portal|host|openhouses|oh-decide|new-address|referral-sign|appt-confirm|stager)(\/|$)/.test(location.pathname);

  // The navy bar follows what is really loading (fifth review round, 09/16/2026). React holds a move to a page
  // whose code is still out (the old screen stays), and says nothing when the person replaces that move with
  // another. So once a different address is actually on screen, the person has gone elsewhere, and the pages
  // opened before this screen was drawn are forgotten (utils/lazyPage.js); their downloads carry on quietly. The
  // mark is read here, while App draws, which is before any page inside this screen is drawn: a page opened as
  // part of the screen that just appeared (a dashboard's first page, behind its own loading text) stays counted.
  // Pop-ups closed while their code was out are not caught this way; the bar then goes when that code arrives or
  // fails for good.
  const openMarkForThisDraw = pageOpenMark();
  const addressOnScreen = useRef(location.pathname);
  useEffect(() => {
    if (addressOnScreen.current === location.pathname) return;
    addressOnScreen.current = location.pathname;
    forgetWaitingPages(openMarkForThisDraw);
  }, [location.pathname, openMarkForThisDraw]);

  // The way back to the agent website is for the login page only: once the address is anything else (the agent
  // website itself, or a staff member who was already signed in somewhere else), it is spent.
  useEffect(() => {
    if (returnToAgentSite && location.pathname !== "/") setReturnToAgentSite(false);
  }, [returnToAgentSite, location.pathname]);

  // The helpers below touch only refs and state setters, so the copies the sign-in listener
  // keeps from the first render behave exactly like the latest ones.

  // Opens or closes the Can't Reach screen. Closing it, whichever way that happens, also
  // clears its "Last tried at" line (09/16/2026, second review round). It used to be cleared
  // only by a Retry that got through and by Sign Out, so the next visit to the screen in the
  // same session could open already naming an old time, before anything had been tried.
  //
  // Why it shows (third review round): "record" when the account's record could not be read,
  // "no-answer" when Firebase's sign-in never answered at all, where the screen can say less.
  const showCantReach = (show, reason = "record") => {
    cantReachShowing.current = show;
    setUnreachable(show);
    if (show) {
      cantReachWhy.current = reason;
      setCantReachReason(reason);
    }
    if (!show) setLastFailedAt(null);
  };

  // Calls off a reload that is waiting for the connection, and the login page's note with it.
  const stopWaitingForSignal = () => {
    reloadWaitingForSignal.current?.();
    reloadWaitingForSignal.current = null;
    setGoogleWaitsForSignal(false);
  };

  // The one reload after a sign-out, so the login page's Continue with Google button has
  // Google's popup helper again (utils/lastRole.js decides when it is due; utils/reloadPage.js
  // keeps it to one, whatever asks).
  //
  // 09/16/2026, second review round. With no signal that reload cannot load the app, and the
  // phone was left on the browser's "not connected" page, which has no buttons: the very dead
  // end the Can't Reach screen's Sign Out exists to avoid, and no signal is exactly when that
  // screen shows. So with no signal the login page stays up, ready for email and password the
  // moment the signal is back, and the reload waits for the browser's "online" event. The
  // login page says meanwhile that Google sign-in is waiting for the connection too.
  //
  // Third review round: iOS may not pass the "online" event on to an installed app that was in
  // the background when the signal came back, so coming back to the app with a signal counts too.
  //
  // Fifth review round: the browser's "online" only says a network is attached. On one bar that passes nothing
  // it still says online, and the reload then ended on that same "not connected" page (a case the owner had
  // accepted). So with the browser online, our own site is asked first (utils/siteReachable.js), and no answer
  // is treated like no signal. The wait is the one page downloads use (utils/lazyPage.js): it ends when the
  // browser is online, the app is on screen and the site answers, and it asks on "online", on coming back to
  // the app, and every 15 seconds.
  const reloadForGoogleSignIn = (notice) => {
    stopWaitingForSignal();
    const reloadNow = () => {
      stopWaitingForSignal();
      reloadPage({ notice });
    };
    const waitForAUsableSignal = () => {
      reloadWaitingForSignal.current = whenSignalIsUsable(reloadNow);
      setGoogleWaitsForSignal(true);
    };
    if (browserIsOffline()) {
      waitForAUsableSignal();
      return;
    }
    // Asking the site takes a moment. Someone signing in meanwhile calls this off (stopWaitingForSignal).
    let calledOff = false;
    reloadWaitingForSignal.current = () => {
      calledOff = true;
    };
    Promise.resolve()
      .then(() => canReachSite())
      .catch(() => false)
      .then((answered) => {
        if (calledOff) return;
        reloadWaitingForSignal.current = null;
        if (answered === true && !browserIsOffline()) reloadNow();
        else waitForAUsableSignal();
      });
  };

  // Everything the app does once nobody is signed in any more: when Firebase reports it
  // (which includes just after signing out), and when Sign Out on the Can't Reach screen
  // stops waiting for Firebase.
  const settleSignedOut = () => {
    // The last person's sign-in downloads and remembered client names go with them.
    clearLaunchData();
    clearContactCache();
    // And so does anything they opened that is still loading: the navy bar no longer speaks for it, and the
    // login page's own note about Google sign-in can show (fifth review round). Every way of signing out ends here.
    forgetWaitingPages();
    // A change still waiting for the signal (utils/saveWhenConnected.js) would only be refused now that
    // nobody is signed in: it is let go, its screen says so, and the browser's "leave this page?"
    // question goes with it, before the reload below (10/05/2026).
    forgetWaitingSaves();
    // Wipes the remembered role and the signed-in-before note together.
    forgetLastRole();
    // This launch started without Google's popup helper, which the login page's Continue
    // with Google button needs. Reload once so it has it. Only when the note is confirmed
    // gone, so the reloaded app starts with the helper and can never reload again
    // (utils/lastRole.js). The login page shows meanwhile. reloadPage also refuses a second
    // reload within a minute, on its own. A deleted or disabled account's message goes with
    // the reload, and is used up here either way.
    const notice = blockedNotice.current;
    blockedNotice.current = null;
    if (shouldReloadForGoogleSignIn({ authStartedWithoutPopupHelper })) {
      reloadForGoogleSignIn(notice);
    }
    dispatch({ type: actionType.SET_USER, payload: null });
    setUser(null);
    setMfaOk(true);
    showCantReach(false);
  };

  // Puts Sign Out on the Can't Reach screen back to ready.
  const finishSigningOut = () => {
    clearTimeout(signOutWait.current);
    signOutWait.current = null;
    signingOutNow.current = false;
    setSigningOut(false);
  };

  // The sidebar's Sign Out.
  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success("Logged out successfully!");
      setUser(null);
      dispatch({ type: actionType.SET_USER, payload: null });
    } catch (error) {
      toast.error("Logout failed. Please try again.");
      console.error("Logout Error:", error);
    }
  };

  // Sign Out on the Can't Reach screen (09/16/2026 review; busy state and the wait for
  // Firebase added in the second round, same day). That screen is shown INSTEAD of the route
  // table, so the sign-out has to put it away itself for the login page to be reachable.
  //
  // One of the ways onto that screen is a launch where Firebase's start-up never finished
  // (the 12 second wait below). There the sign-out waits in line behind that start-up, maybe
  // for ever, so the button says "Signing Out..." and takes no second tap, and after
  // SIGN_OUT_WAIT_MS the login page shows anyway, with nothing of the account kept on this
  // device. The sign-out itself stays in line and finishes if the start-up ever does; a
  // signed-in answer that arrives before it is not let in (leavingAccount).
  const signOutFromCantReach = async () => {
    if (signingOutNow.current) return;
    signingOutNow.current = true;
    setSigningOut(true);
    leavingAccount.current = true;
    // A retry still out, or one about to start, lets nobody in from here on.
    const leaving = signedInUser.current;
    signedInUser.current = null;
    let shownWithoutFirebase = false;
    signOutWait.current = setTimeout(() => {
      shownWithoutFirebase = true;
      finishSigningOut();
      settleSignedOut();
      toast.success("Logged out successfully!");
    }, SIGN_OUT_WAIT_MS);
    try {
      await signOut(auth);
      leavingAccount.current = false;
      if (shownWithoutFirebase) return;
      finishSigningOut();
      toast.success("Logged out successfully!");
      // Firebase reports nobody signed in as it signs out, and that report does the rest
      // (settleSignedOut, in the listener below). These show the login page even if the
      // report is still on its way.
      setUser(null);
      dispatch({ type: actionType.SET_USER, payload: null });
      showCantReach(false);
    } catch (error) {
      leavingAccount.current = false;
      console.error("Logout Error:", error);
      // Already on the login page: nothing on it could act on this message.
      if (shownWithoutFirebase) return;
      finishSigningOut();
      // Still signed in, so Retry can try again for them.
      if (!signedInUser.current) signedInUser.current = leaving;
      toast.error("Logout failed. Please try again.");
    }
  };

  useEffect(() => {
    const auth = getAuth();

    // Safety net: if auth never reports back (e.g. iOS Safari blocking the
    // IndexedDB-backed persistence), don't strand the user on the loading
    // screen forever: stop waiting after a few seconds.
    //
    // WHERE it stops, 09/16/2026 review. It used to drop everyone on the login
    // page, which on a phone that IS signed in is exactly the misleading screen
    // the Can't Reach screen exists to avoid: signing in again needs the signal
    // that is missing. A device carrying the signed-in-before note is one where
    // somebody has signed in here (utils/lastRole.js), so it gets Can't Reach,
    // with its Retry and, importantly, its Sign Out. A device nobody has ever
    // signed in on has nothing to be signed in AS, so it still gets the login
    // page. Either way this is only what shows while the wait is over: a late
    // answer from Firebase takes over below, in both directions.
    const loadingTimeout = setTimeout(() => {
      console.warn("[App] Auth state did not resolve in time.");
      if (readSignedInBefore()) showCantReach(true, "no-answer");
      setLoading(false);
    }, 12000);

    // Lets a signed-in person in, or refuses them. Runs when Firebase reports who is
    // signed in, and again from the Can't Reach screen (retrySignIn below).
    const admitSignedInUser = async (user) => {
        // Which order this one launch uses, settled here, once, before anything starts.
        //
        // Every deal and the team list start downloading the moment sign-in is confirmed, and
        // the first screens reuse them (utils/launchData.js). Any account that is then refused
        // throws them away again, below.
        //
        // 09/16/2026, the owner's call after live measurements. On 09/15 those downloads shared
        // a Firestore connection with the person's own record, and that connection releases a
        // single read only once every read on it has caught up: the record waited for all 492
        // deals (about 30 MB) and "Loading..." covered the whole download (page frame at 3.3 s
        // instead of 1.8 s). Starting the downloads after the record fixed the frame (1.9 s) but
        // put the numbers on screen 1.1-1.4 s later.
        //
        // The record now travels on a quick connection of its own (utils/fastUserRead.js), so it
        // no longer shares their queue and both can start at once — but only while that quick
        // connection's own code is actually here. When it is not, the record falls back to the
        // shared connection, and starting 30 MB of deals ahead of it would rebuild the 09/15
        // fault: about 1.4 s WORSE than the order this replaced. That is likeliest on the first
        // launch after an upload, where the code has a new name and so a cold download, and on a
        // weak signal, where it is slow for the same reason the deals queue is long. So a launch
        // without it keeps the 09/15 order: the record first, the downloads once the account is
        // let in (below). Asking costs nothing — no download is started and nothing is waited on.
        //
        // The trade-off the owner accepted on 09/16/2026. On this fast lane the deals and team
        // downloads start BEFORE the deleted or disabled check further down, so an account that has
        // been switched off still RECEIVES that data for the moment it takes to read their record,
        // and is only then signed out and sent to the login page. It is thrown away here
        // (clearLaunchData) and never reaches a screen, but it did reach the device. Henry weighed
        // that against the speed and chose to keep the speed.
        // The real long-term answer is a rules-level fix, not a change here: Firestore rules that
        // refuse a type 1 (deleted) or type 2 (disabled) account, so the download is denied at the
        // server and a switched-off account never receives the data in the first place.
        const onTheFastLane = isFastLaneReady();
        if (onTheFastLane) startLaunchReads(user.uid);
        try {
          const { user: userInfo, unreachable: cannotReach } =
            await loadSignedInUserFast(user.uid);
          // Firebase reported someone else, or nobody, while the lookup was out, so
          // its answer no longer applies.
          if (signedInUser.current?.uid !== user.uid) return;

          // Weak or no signal: the record could not be read, which is not the same as
          // having no record. Stay signed in and show the Can't Reach screen, which
          // tries again, instead of the login page, where signing in needs signal too.
          // Nothing from an earlier sign-in is kept, as for any record that could not be loaded.
          if (cannotReach) {
            lastAttemptReached.current = false;
            clearLaunchData();
            clearContactCache();
            dispatch({ type: actionType.SET_USER, payload: null });
            setUser(null);
            showCantReach(true);
            return;
          }
          lastAttemptReached.current = true;
          // Also what clears the "Last tried at" line when a sign-in that answered late, after
          // the 12 second wait, gets through (09/16/2026, second review round).
          showCantReach(false);
          // if (userInfo.role === "Accountant") {
          //   await auth.signOut();
          //   return;
          // }

          // No record, deleted or disabled: nobody gets in, so the sign-in downloads
          // started above are thrown away, along with any client names remembered on
          // this device.
          if (!userInfo || userInfo.type === 1 || userInfo.type === 2) {
            clearLaunchData();
            clearContactCache();
          }

          // 🔒 BLOCK deleted user
          if (userInfo?.type === 1) {
            const message =
              "Your account has been deleted. Kindly contact Administrator";
            toast.error(message);
            blockedNotice.current = message;
            forgetLastRole();
            await auth.signOut();
            dispatch({ type: actionType.SET_USER, payload: null });
            setUser(null);
            setLoading(false);
            return;
          }

          // 🔒 BLOCK disabled user
          if (userInfo?.type === 2) {
            const message =
              "Your account has been disabled. Kindly contact Administrator";
            toast.error(message);
            blockedNotice.current = message;
            forgetLastRole();
            await auth.signOut();
            dispatch({ type: actionType.SET_USER, payload: null });
            setUser(null);
            setLoading(false);
            return;
          }

          // ✅ Allowed user. On the express order the sign-in downloads started at the top of
          // this function are still running, or already done. On the 09/15 order they start now:
          // behind the person's own record instead of in front of it, and only for an account
          // that is really let in, so a refused or missing record never starts them at all.
          // Either way the dashboard's first screens reuse them (utils/launchData.js).
          if (!onTheFastLane && userInfo) startLaunchReads(user.uid);
          // Trust the VERIFIED auth-token email over the mutable Firestore
          // `email` field, so super-admin (isSuperAdminOwner, keyed on email)
          // can never be spoofed by editing a users doc. Falls back to the doc
          // email only if the auth email is somehow absent.
          dispatch({
            type: actionType.SET_USER,
            payload: { ...userInfo, email: user.email || userInfo?.email },
          });
          // Lets the next launch start this person's first screen downloading at
          // once (utils/lastRole.js). Written only here, from the fresh user record,
          // so a demo "View As" role switch, or the owner's View As (utils/roles.js
          // applyViewAs), is never remembered.
          rememberLastRole(userInfo?.role);
          // And that someone has signed in here, so the next launch starts Firebase
          // without Google's popup helper (firebase/firebaseConfig.js).
          rememberSignedInBefore();
          loadEmailTemplates().then((tmap) => {
            dispatch({ type: actionType.SET_EMAIL_TEMPLATES, payload: tmap });
          });
          loadTeamDefaults().then((td) => {
            dispatch({ type: actionType.SET_TEAM_DEFAULTS, payload: td });
          });
          setMfaOk(
            MFA_ENROLLMENT_PAUSED ||
              isMfaEnrolled(user) ||
              // "Continue for now" was used this browser session (MFA not
              // enabled on the Firebase project) — don't re-gate on reload.
              (typeof sessionStorage !== "undefined" &&
                sessionStorage.getItem("mfaSetupIncompleteBypass") === "1"),
          );
          setReturnToAgentSite(takeReturnTo());
          setUser(userInfo);
        } catch (error) {
          console.error("Error fetching user info:", error);
          // The server answered, with something unexpected rather than "cannot be
          // reached", so this attempt is not one to report as a failed retry.
          lastAttemptReached.current = true;
          clearLaunchData();
          clearContactCache();
          forgetLastRole();
          await auth.signOut();
          dispatch({ type: actionType.SET_USER, payload: null });
          setUser(null);
          showCantReach(false);
        }
    };
    retrySignIn.current = async () => {
      if (signedInUser.current) await admitSignedInUser(signedInUser.current);
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      clearTimeout(loadingTimeout);
      firebaseAnswered.current = true;
      if (user && leavingAccount.current) {
        // The account being left, reported by a Firebase start-up that finished while
        // Sign Out on the Can't Reach screen waited in line behind it. The sign-out comes
        // next and reports nobody; nobody is let in meanwhile.
        signedInUser.current = null;
        setLoading(false);
        return;
      }
      signedInUser.current = user || null;
      if (user) {
        // Someone is signed in, so a reload still waiting for the signal would only throw
        // them out of the app they just got into.
        stopWaitingForSignal();
        await admitSignedInUser(user);
      } else {
        // Nobody is signed in, which includes just after signing out.
        leavingAccount.current = false;
        finishSigningOut();
        settleSignedOut();
      }

      setLoading(false);
    });

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
      // Nothing left waiting to act on a page that is gone.
      clearTimeout(signOutWait.current);
      reloadWaitingForSignal.current?.();
      reloadWaitingForSignal.current = null;
    };
  }, []);

  // Foreground push messages. Guarded: `messaging` can be unavailable on
  // browsers that don't support FCM (e.g. iOS Safari outside an installed
  // PWA), and this must run as an effect — calling onMessage in the render
  // body re-subscribed a new listener on every render.
  useEffect(() => {
    let unsubscribe;
    // `messaging` initializes asynchronously (gated on isSupported), so wait for
    // it before subscribing. No-ops on browsers without push support.
    messagingReady.then((m) => {
      if (m) {
        unsubscribe = onMessage(m, (payload) => {
          // A text alert links to its conversation (data.url); clicking the
          // toast goes there, like tapping the phone alert (punch list line 34).
          const route = appRouteFromAlertUrl(payload?.data?.url);
          toast(
            <Message notification={payload.data} />,
            route
              ? {
                  onClick: () => {
                    window.location.hash = route;
                  },
                  // A swipe ends in onClick too, and must not open (and so
                  // mark read) a conversation nobody chose.
                  draggable: false,
                }
              : undefined,
          );
        });
      }
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // The master texting switch (utils/textingSwitch.js). Watched only once
  // somebody is signed in: reading Firestore before sign-in is the exact shape
  // that hung the login screen in August, and the rule on this doc needs a
  // signed-in staff member anyway. Keeping the answer in state re-renders the
  // tree, so flipping the switch in Settings clears the sidebar link, the deal
  // Texts tab and the Home card without anyone reloading.
  const [, setTextingOn] = useState(isTextingEnabled());
  useEffect(() => {
    if (!user) return undefined;
    const stopWatching = watchTextingFlag();
    const stopListening = subscribeTextingFlag(setTextingOn);
    return () => {
      if (typeof stopWatching === "function") stopWatching();
      stopListening();
    };
  }, [user]);

  // A switched-off account, mid-session (utils/accountWatch.js, punch list
  // line 38). The launch-time check above only ever ran once, so Delete or
  // Disable in User Management left an open tab looking normal while every
  // save it made was refused. This watches the person's own record and ends
  // the session the same way the launch check does: the message rides the
  // sign-out (blockedNotice -> settleSignedOut) so the login page says why.
  // In the owner's View As a person (utils/roles.js applyViewAsPerson) the user in memory
  // is a copy of THEIR record, so this keeps watching his own (realUser).
  const ownAccountId = user?.realUser?.id || user?.id;
  useEffect(() => {
    if (!ownAccountId) return undefined;
    return watchOwnAccount(ownAccountId, async (message) => {
      toast.error(message);
      blockedNotice.current = message;
      forgetLastRole();
      try {
        await auth.signOut();
      } catch (e) {
        console.error("sign-out after account switched off failed:", e);
      }
      dispatch({ type: actionType.SET_USER, payload: null });
      setUser(null);
    });
  }, [ownAccountId]);

  // A message kept through the one sign-out reload (a deleted or disabled account,
  // utils/reloadPage.js). Shown once the loading screen is gone, so the message area
  // is on the page to show it.
  useEffect(() => {
    if (loading) return;
    const notice = takeReloadNotice();
    if (notice) toast.error(notice);
  }, [loading]);







  // One try at a time, from the Retry button or by itself (below).
  // Never while Sign Out is running: the person is leaving.
  const retry = useCallback(async () => {
    if (retryInFlight.current || signingOutNow.current) return;
    retryInFlight.current = true;
    setRetrying(true);
    try {
      await retrySignIn.current?.();
    } finally {
      retryInFlight.current = false;
      setRetrying(false);
      // What the screen says about this attempt. An attempt that got through clears
      // the line rather than leaving a stale time behind for the next bad patch of
      // signal. An attempt that could not even be made (a launch where Firebase's
      // sign-in never answered, so there is nobody to let in) counts as failed, which
      // is what the person tapping Retry there is seeing. And an attempt that ends with the
      // screen already closed (Firebase reported nobody signed in meanwhile) leaves no time
      // behind for the next visit.
      setLastFailedAt(
        lastAttemptReached.current || !cantReachShowing.current ? null : new Date(),
      );
    }
  }, []);

  // The Retry button itself (fourth review round, 09/16/2026). When Firebase's sign-in never
  // answered at all ("no-answer"), there is nobody to let in yet, so a try can never succeed and
  // each tap only changed the time on "Last tried at". The one real retry after a start-up that
  // hung is a fresh start, so with a signal the tap reloads the app (utils/reloadPage.js, at most
  // once a minute), and the button says it is working meanwhile. With no signal a reload would only
  // reach the browser's "not connected" page, so the tap is a try as before, and says it failed. So
  // is a tap the reload guard refuses, rather than a tap that does nothing. Only a tap reloads: the
  // tries this screen makes by itself (below) never do.
  //
  // Fifth review round, same day:
  //   - the browser's "online" is not enough: this screen most often shows on a connection that passes
  //     nothing, and a reload there ends on that same "not connected" page. So our own site is asked
  //     first (utils/siteReachable.js, up to about 4 seconds, with the button saying it is working), and
  //     only an answer reloads; otherwise the tap is a try, which says it failed;
  //   - only while Firebase has not answered at all. Once it has, the account's record is being read (the
  //     screen still says "could not be checked yet" until that read finishes), and a reload would throw
  //     that read away with the sign-in downloads already started. A tap then is a normal try;
  //   - the reload has its own once-a-minute stamp, so a Sign Out soon after still gets the reload that
  //     brings Google sign-in back.
  const reloadIsTheRetry = () =>
    cantReachShowing.current &&
    cantReachWhy.current === "no-answer" &&
    !firebaseAnswered.current &&
    !browserIsOffline() &&
    !signingOutNow.current;
  const tapRetry = async () => {
    if (!reloadIsTheRetry()) {
      retry();
      return;
    }
    if (retryInFlight.current) return;
    retryInFlight.current = true;
    setRetrying(true);
    let answered = false;
    try {
      answered = (await canReachSite()) === true;
    } catch {
      answered = false;
    } finally {
      retryInFlight.current = false;
    }
    if (answered && reloadIsTheRetry() && reloadPage({ key: RETRY_RELOAD_AT_KEY })) return;
    setRetrying(false);
    // The screen closed while the site was asked (Firebase let the person in, or they signed out): nothing to try.
    if (!cantReachShowing.current) return;
    // retry() says it is working again at once, unless something else is already running.
    retry();
  };

  // While the Can't Reach screen shows, try again by itself when the connection comes
  // back, or when the app is brought back to the screen (a phone often regains signal
  // while the app sits in the background).
  //
  // Fourth review round: an "online" that arrives while the app is in the background is
  // ignored, as the other waits for the signal do. iOS can pause the page halfway through a try
  // started there, and the screen then reported a failure with the signal back. Coming back to
  // the app tries instead (onVisible).
  useEffect(() => {
    if (!unreachable) return undefined;
    const onOnline = () => {
      if (document.visibilityState === "hidden") return;
      retry();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") retry();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [unreachable, retry]);

  if (loading) {
    // A client opening their link waits on the portal's own navy screen; standalone, because nothing else
    // has painted the document yet. Staff keep the plain text.
    if (onPortal) return <PortalLoading standalone />;
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  // Signed in, but the server could not be reached to load the account. A client's
  // portal link, an agent's hosting link and a seller's decision link never wait on
  // this: none of them needs a staff account.
  if (unreachable && !onPublicPage) {
    return (
      <main className="w-full">
        <CantReachScreen
          retrying={retrying}
          signingOut={signingOut}
          onRetry={tapRetry}
          onSignOut={signOutFromCantReach}
          lastFailedAt={lastFailedAt}
          reason={cantReachReason}
        />
      </main>
    );
  }

  // A redirect draws nothing, and React Router runs the move it starts as a transition: React
  // keeps the redirect on screen until the next page's code has arrived. With no signal that
  // is as long as the signal is gone, so a bare redirect left a blank white page (fourth review
  // round, 09/16/2026). Each redirect below therefore draws the loading screen, with its
  // no-signal note. On a good connection that shows for about one frame.

  // ✅ Redirect Logged-in Users to Dashboard
  const PublicRoute = ({ children }) => {
    // Back from the CRM login the agent website sent them to: the agent website again, where ?crm=1 signs
    // them in with this CRM login.
    if (user && mfaOk && returnToAgentSite) {
      return (
        <>
          <Navigate to="/openhouses?crm=1" replace />
          <ScreenLoading />
        </>
      );
    }
    return user ? (
      <>
        {/* Everyone opens on the Dashboard, Client Care included (their My Checklist is
            there; Transactions stays in the menu). Spec 09/27/2026. */}
        <Navigate to="/dashboard" replace />
        <ScreenLoading />
      </>
    ) : (
      children
    );
  };

  // 🚧 Handle Invalid Routes
  const NotFoundRoute = () => (
    <>
      <Navigate to={user ? "/dashboard" : "/"} />
      <ScreenLoading />
    </>
  );

  return (
    <main className="w-full">
     
      {/* Each screen's code downloads on first open; this shows while it arrives. */}
      <Suspense
        fallback={
          onPortal ? <PortalLoading standalone /> : <ScreenLoading />
        }
      >
      <Routes>
        <Route
          path="/"
          element={
            <PublicRoute>
              {/* <Signup /> */}
              <Login />
            </PublicRoute>
          }
        />
        {/* Signup creates an Admin account. Enabled in LOCAL DEV ONLY (for
            seeding the sandbox); in a production build it redirects away so
            there's no open admin signup on the live site. */}
        <Route
          path="/signup"
          element={
            process.env.NODE_ENV === "development" ? <Signup /> : <NotFoundRoute />
          }
        />
        {/* Signed-out visitors go to the login page — mounting the dashboard
            without auth fires Firestore reads that the rules deny (the
            "Missing or insufficient permissions" popups). Guarded with a
            plain ternary, NOT an inline wrapper component: a wrapper defined
            inside App gets a new identity on every render, which remounts the
            whole dashboard on each context update (visible as flashing). */}
        <Route
          path="/dashboard/*"
          element={
            user ? (
              mfaOk ? (
                <Dashboard onLogout={handleLogout} />
              ) : (
                // Logged in but no second factor yet: enroll before the
                // dashboard. onEnrolled clears the gate for this session.
                <MfaEnrollGate onEnrolled={() => setMfaOk(true)} />
              )
            ) : (
              // Signed out (the sidebar's Sign Out, say): the login page may still be downloading.
              <>
                <Navigate to="/" replace />
                <ScreenLoading />
              </>
            )
          }
        />
        <Route path="/PrivacyPolicy" element={<Privacypolicy />} />
        <Route path="/TermsAndService" element={<Termsandservice />} />
        <Route path="/feedback" element={<PublicFeedback />} />
        <Route path="/portal" element={<ClientPortal />} />
        <Route path="/openhouses" element={<AgentSite />} />
        {/* The retired hosting link (/#/host?t=...) lands on the agent website's welcome page; the token is
            ignored for good. */}
        <Route
          path="/host"
          element={
            <>
              <Navigate to="/openhouses" replace />
              <ScreenLoading />
            </>
          }
        />
        <Route path="/oh-decide" element={<OhDecide />} />
        <Route path="/new-address" element={<NewAddress />} />
        <Route path="/referral-sign" element={<ReferralSign />} />
        <Route path="/appt-confirm" element={<ApptConfirm />} />
        <Route path="/stager" element={<StagerPortal />} />
        <Route path="*" element={<NotFoundRoute />} />
      </Routes>
      </Suspense>
      {onPublicPage ? null : <NewVersionBanner />}
      {/* The "look before it sends" popup for every email a button sends (09/19/2026). Signed-in
          team only; on the portal, kiosk and other public pages emails go straight out as before. */}
      {user && mfaOk && !onPublicPage ? <EmailReviewHost /> : null}
      {/* The navy bar at the bottom (fourth review round, 09/16/2026). Outside the loading screen on
          purpose: when a page someone opened waits for the signal, React keeps the old page up and no
          loading screen appears, and a pop-up form waits behind an empty one, so the bar says so.
          It also carries the login page's note while the one reload after a sign-out waits for the
          connection (reloadForGoogleSignIn): Continue with Google cannot work yet, so the page says so
          rather than letting it fail; email and password need no reload. One message at a time, and
          none while the loading screen's own note is up (components/NoSignalBar.jsx). Never on a
          client's portal, an agent's hosting page or a seller's decision page. */}
      {onPublicPage ? null : (
        <NoSignalBar googleWaits={googleWaitsForSignal && !user && location.pathname === "/"} />
      )}
      <ToastContainer
           position="top-right"
          autoClose={3000}
          hideProgressBar={true}
       
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          type="error"
          pauseOnHover
          theme="light"
      />
    </main>
  );
}

export default App;
